import { FC, ReactElement, useState, KeyboardEvent, ChangeEvent } from 'react';
import { NavigateFunction, useNavigate, useParams } from 'react-router';
import i18next from 'i18next';
import { HandThumbsDown, HandThumbsUp, Heart, HeartFill } from 'react-bootstrap-icons';
import { range } from 'lodash';
import { Alert, Form, Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { PiHandWaving } from 'react-icons/pi';
import { FeedbackRequest } from '../../../../shared/requests/feedback.request';
import { fetchFromRestAPI } from '../../util/api';
import { AUTH0_ORGANIZATION_ID } from '../../config';

const CallFeedback: FC = (): ReactElement => {
  const { code, isTherapist } = useParams<{ code: string, isTherapist: string }>()
  const navigate: NavigateFunction = useNavigate();

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const [rating, setRating] = useState(4);
  const [ratingGiven, setRatingGiven] = useState(false);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [improve, setImprove] = useState<string | undefined>(undefined);
  const [toImprove, setToImprove] = useState(false);

  const goBackUrl: string = `/call/${code}`;
  
  const handleRatingClick = (index: number): void => {
    setRating(index);
    setRatingGiven(true);
  }
  
  const handleRatingKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRatingClick(index);
    }
  }

  const handleSubmit = async (): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);
    
    if (!code) {
      return;
    }
    
    try {
      const query: URLSearchParams = new URLSearchParams({ tenant: AUTH0_ORGANIZATION_ID });
      const url: string = `/api/v1/feedback?${query.toString()}`
      const request: FeedbackRequest = { roomCode: code, rating: rating, message: message, improve: improve };
      await fetchFromRestAPI(url, { method: 'POST', body: request });
      navigate(isTherapist === '1' ? '/patients' : '/')
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleContinue = (): void => {
    navigate(isTherapist === '1' ? '/patients' : '/')
  }
  
  return (
    <main
      className="d-flex flex-column flex-grow-1 align-items-center justify-content-center px-3 py-4 py-md-5 text-white"
      style={{
        background: 'linear-gradient(180deg, #002b4b 0%, #0a3b61 45%, #11486f 100%)',
        minHeight: '100%',
      }}
    >
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}
      
      <div className="w-100 mx-auto" style={{ maxWidth: '760px' }}>
        <div
          className="rounded-4 shadow-lg px-4 px-md-5 py-4 py-md-5"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.12)'
          }}
        >
          <div className="text-center mb-4">
            <p className="mb-0" style={{ lineHeight: 1.5, fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.9)' }}>
              {i18next.t('callFeedback.leftMessage')}
              <br />
              {i18next.t('callFeedback.rejoinQuestion')}{' '}
              <a href={goBackUrl} className="text-white text-decoration-underline fw-semibold">
                {i18next.t('callFeedback.rejoinLink')}
              </a>
            </p>
          </div>

          <>
            <div className="text-center mb-4 mb-md-3">
              <h1 className="fw-bold mb-3" style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', lineHeight: 1.05, letterSpacing: '-0.02rem' }}>
                {i18next.t('callFeedback.ratingTitle')}
              </h1>

              <p
                className="mb-0 mx-auto"
                style={{
                  maxWidth: '520px',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  color: 'rgba(255, 255, 255, 0.82)'
                }}
              >
                {i18next.t('callFeedback.ratingSubtitle')}
              </p>
            </div>

            <section className="mb-4 mb-md-5">
              <div className="d-flex justify-content-center flex-wrap gap-2 gap-md-3">
                {range(0, 5).map((index: number) => {
                  const isActive: boolean = index <= rating;

                  return (
                    <button
                      key={index}
                      type="button"
                      className="btn d-inline-flex align-items-center justify-content-center rounded-circle"
                      onClick={() => handleRatingClick(index)}
                      onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => handleRatingKeyDown(event, index)}
                      onMouseEnter={() => {
                        if (!ratingGiven) {
                          setRating(index);
                        }
                      }}
                      aria-label={i18next.t('callFeedback.ratingAriaLabel', { value: index + 1 })}
                      style={{
                        width: '56px',
                        height: '56px',
                        border: isActive ? '1px solid rgba(255, 105, 95, 0.7)' : '1px solid rgba(255, 255, 255, 0.16)',
                        backgroundColor: isActive ? 'rgba(255, 105, 95, 0.16)' : 'rgba(255, 255, 255, 0.06)',
                        color: '#ff695f',
                        fontSize: '1.25rem',
                        transition: 'all 0.2s ease',
                        boxShadow: isActive ? '0 8px 24px rgba(255, 105, 95, 0.18)' : 'none'
                      }}
                    >
                      {isActive ? <HeartFill aria-hidden="true" /> : <Heart aria-hidden="true" />}
                    </button>
                  )
                })}
              </div>
            </section>

            {isTherapist === '0' && (
              <section className="mb-4">
                <div
                  className="rounded-4 p-3 p-md-4"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.10)' }}
                >
                  <div className="mb-2 fw-semibold">
                    {i18next.t('callFeedback.messageTitle')}
                  </div>
                  <div className="mb-3" style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.75)' }}>
                    {i18next.t('callFeedback.messageHint')}
                  </div>

                  <Form.Control
                    value={message}
                    as="textarea"
                    rows={4}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setMessage(event.target.value)}
                    placeholder={i18next.t('callFeedback.messagePlaceholder')}
                    className="border-0"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      borderRadius: '16px',
                      minHeight: '120px',
                    }}
                  />
                </div>
              </section>
            )}

            <section className="mb-4">
              <div
                className="rounded-4 p-3 p-md-4"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.10)' }}
              >
                <div className="fw-semibold mb-2">
                  {i18next.t('callFeedback.callQualityTitle')}
                </div>
                <div className="mb-3" style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.75)' }}>
                  {i18next.t('callFeedback.callQualityHint')}
                </div>

                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <button
                    type="button"
                    className={`btn px-4 py-2 rounded-pill fw-semibold ${!toImprove ? 'btn-light' : 'btn-outline-light'}`}
                    onClick={() => setToImprove(false)}
                    style={{ minWidth: '120px' }}
                  >
                    <HandThumbsUp className="me-2" aria-hidden="true" />
                    {i18next.t('generic.yes')}
                  </button>

                  <button
                    type="button"
                    className={`btn px-4 py-2 rounded-pill fw-semibold ${toImprove ? 'btn-light' : 'btn-outline-light'}`}
                    onClick={() => setToImprove(true)}
                    style={{ maxWidth: '120px' }}
                  >
                    <HandThumbsDown className="me-2" aria-hidden="true" />
                    {i18next.t('generic.no')}
                  </button>
                </div>
              </div>
            </section>

            {toImprove && (
              <section className="mb-4 mb-md-5">
                <div
                  className="rounded-4 p-3 p-md-4"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.10)' }}
                >
                  <div className="mb-2 fw-semibold">
                    {i18next.t('callFeedback.improveTitle')}
                  </div>
                  <div className="mb-3" style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.75)' }}>
                    {i18next.t('callFeedback.improveHint')}
                  </div>

                  <Form.Control
                    value={improve}
                    as="textarea"
                    rows={4}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setImprove(event.target.value)}
                    placeholder={i18next.t('callFeedback.improvePlaceholder')}
                    className="border-0"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      borderRadius: '16px',
                      minHeight: '120px'
                    }}
                  />
                </div>
              </section>
            )}

            <div className="text-center">
              <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                <Button
                  variant="light"
                  className="rounded-pill px-4 px-md-5 py-2 py-md-3 fw-bold shadow"
                  onClick={() => void handleSubmit()}
                  style={{
                    color: '#002b4b',
                    minWidth: '240px',
                    fontSize: '1rem',
                  }}
                >
                  {i18next.t('callFeedback.submit')}
                </Button>

                <Button
                  variant="outline-light"
                  className="rounded-pill px-4 px-md-5 py-2 py-md-3 fw-semibold"
                  onClick={handleContinue}
                  style={{
                    minWidth: '240px',
                    fontSize: '1rem',
                  }}
                >
                  {i18next.t('callFeedback.skip')}
                </Button>
              </div>

              <h2
                className="mt-4 mb-0 fw-bold"
                style={{
                  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                  lineHeight: 1.3,
                  color: 'rgba(255, 255, 255, 0.95)',
                }}
              >
                {i18next.t('callFeedback.footerText')}
                <PiHandWaving aria-hidden="true" />
              </h2>
            </div>
          </>
        </div>
      </div>
    </main>
  )
}

export default CallFeedback
